/* Reveal-on-scroll trigger. All animation lives in motion.css as transitions;
   this script only decides WHEN each root flips from pending to revealed:
   above-fold roots show statically unless they explicitly opt into an initial
   reveal, while below-fold roots reveal once when they scroll into view.
   Kill-switches: reduced motion, the theme editor, and ?sb-motion=off. */
(() => {
  const ROOT_SELECTOR = '[data-sb-motion="reveal"]';
  const LINKED_ITEM_STATE_ATTR = 'data-sb-motion-linked-state';
  const FIRST_ROW_LINK = 'first-row';
  const REVIEWS_FOOTER_LINK = 'reviews-footer';
  const PROGRESS_ROOT_SELECTOR = '[data-sb-progress-root]';
  const ROW_GROUP_SELECTOR = '[data-sb-motion-row-group]';
  const ROW_SELECTOR = '[data-sb-motion-row]';
  const ROW_ITEM_SELECTOR = '[data-sb-motion-row-item]';
  const REVEAL_ROOT_MARGIN = '0px 0px -10% 0px';
  const PROGRESS_ROOT_MARGIN = '0px';
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let revealObserver = null;
  let progressRevealObserver = null;
  const revealFrames = new WeakMap();

  const motionIsDisabled = () =>
    reducedMotionQuery.matches
    || Boolean(window.Shopify && window.Shopify.designMode)
    || new URL(window.location.href).searchParams.get('sb-motion') === 'off';

  const getLinkedItems = (root) => {
    if (!(root instanceof HTMLElement)) return [];
    if (root.dataset.sbMotionLink === FIRST_ROW_LINK) {
      const group = root.closest(ROW_GROUP_SELECTOR);
      const row = group?.querySelector(ROW_SELECTOR);
      return row ? Array.from(row.querySelectorAll(':scope > [data-sb-motion-row-linked]')) : [];
    }
    if (root.dataset.sbMotionLink === REVIEWS_FOOTER_LINK) {
      const reviews = root.closest('.sb-reviews-list');
      const footer = reviews?.querySelector('.sb-reviews-list__footer');
      return footer instanceof HTMLElement ? [footer] : [];
    }
    return [];
  };

  const cancelReveal = (root) => {
    const frame = revealFrames.get(root);
    if (frame !== undefined) window.cancelAnimationFrame(frame);
    revealFrames.delete(root);
  };

  const clearRootState = (root) => {
    cancelReveal(root);
    revealObserver?.unobserve(root);
    progressRevealObserver?.unobserve(root);
    root.removeAttribute('data-sb-motion-state');
    getLinkedItems(root).forEach((item) => item.removeAttribute(LINKED_ITEM_STATE_ATTR));
  };

  const setRootState = (root, state) => {
    if (!(root instanceof HTMLElement)) return;
    cancelReveal(root);
    root.dataset.sbMotionState = state;
    getLinkedItems(root).forEach((item) => item.setAttribute(LINKED_ITEM_STATE_ATTR, state));
    if (revealObserver && state !== 'pending') revealObserver.unobserve(root);
    if (progressRevealObserver && state !== 'pending') progressRevealObserver.unobserve(root);
  };

  const revealOnNextPaint = (root) => {
    setRootState(root, 'pending');
    revealFrames.set(root, window.requestAnimationFrame(() => {
      revealFrames.set(root, window.requestAnimationFrame(() => {
        revealFrames.delete(root);
        if (!root.isConnected || isHiddenForMotion(root)) {
          clearRootState(root);
          return;
        }
        setRootState(root, motionIsDisabled() ? 'static' : 'revealed');
      }));
    }));
  };

  const isAboveFold = (root) => {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    if (viewportHeight <= 0) return true;
    return root.getBoundingClientRect().top <= viewportHeight * 0.9;
  };

  const isHiddenForMotion = (root) => {
    if (!(root instanceof HTMLElement)) return true;
    if (root.closest('.sb-segment-hidden')) return true;
    return root.getClientRects().length === 0;
  };

  const ensureObserver = () => {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target.isConnected
            && entry.target.dataset.sbMotionState === 'pending' && !isHiddenForMotion(entry.target)) {
            setRootState(entry.target, 'revealed');
          }
        });
      },
      { rootMargin: REVEAL_ROOT_MARGIN, threshold: 0 }
    );
    return revealObserver;
  };

  const ensureProgressObserver = () => {
    if (progressRevealObserver) return progressRevealObserver;
    progressRevealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target.isConnected
            && entry.target.dataset.sbMotionState === 'pending' && !isHiddenForMotion(entry.target)) {
            setRootState(entry.target, 'revealed');
          }
        });
      },
      { rootMargin: PROGRESS_ROOT_MARGIN, threshold: 0 }
    );
    return progressRevealObserver;
  };

  const firstVisualRow = (row) => {
    const items = Array.from(row.querySelectorAll(`:scope > ${ROW_ITEM_SELECTOR}`));
    if (!items.length) return [];
    const firstTop = items[0].offsetTop;
    return items.filter((item) => Math.abs(item.offsetTop - firstTop) <= 1);
  };

  const prepareRowGroup = (group) => {
    const row = group.querySelector(ROW_SELECTOR);
    const linkRoot = group.querySelector(`[data-sb-motion-link="${FIRST_ROW_LINK}"]`);
    if (!(row instanceof HTMLElement) || !(linkRoot instanceof HTMLElement)) return;

    const items = Array.from(row.querySelectorAll(`:scope > ${ROW_ITEM_SELECTOR}`));
    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      item.removeAttribute('data-sb-motion-row-linked');
      item.removeAttribute(LINKED_ITEM_STATE_ATTR);
      if (!item.dataset.sbMotion) item.dataset.sbMotion = 'reveal';
      item.setAttribute('data-sb-motion-item', '');
    });

    const linkedItems = firstVisualRow(row);
    const linkedState = linkRoot.dataset.sbMotionState;
    linkedItems.forEach((item) => {
      clearRootState(item);
      item.removeAttribute('data-sb-motion');
      item.setAttribute('data-sb-motion-row-linked', '');
      if (linkedState) item.setAttribute(LINKED_ITEM_STATE_ATTR, linkedState);
    });
  };

  const prepareRowGroups = (scope) => {
    const groups = Array.from(scope.querySelectorAll(ROW_GROUP_SELECTOR));
    if (scope instanceof Element && scope.matches(ROW_GROUP_SELECTOR)) groups.unshift(scope);
    groups.forEach(prepareRowGroup);
  };

  const collectRoots = (scope) => {
    const roots = Array.from(scope.querySelectorAll(ROOT_SELECTOR));
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.unshift(scope);
    return roots;
  };

  const initScope = (scope) => {
    prepareRowGroups(scope);
    const roots = collectRoots(scope);
    roots.forEach((root) => {
      if (!(root instanceof HTMLElement) || root.dataset.sbMotionState) return;
      if (isHiddenForMotion(root)) return;
      if (root.hasAttribute('data-sb-motion-stagger')) {
        const items = Array.from(root.querySelectorAll('[data-sb-motion-item]'))
          .filter((item) => item.closest(ROOT_SELECTOR) === root);
        items.forEach((item, index) => {
          item.style.setProperty('--sb-motion-stagger-index', String(Math.min(index, 5)));
        });
      }
      if (motionIsDisabled() || typeof IntersectionObserver !== 'function') {
        setRootState(root, 'static');
        return;
      }
      if (isAboveFold(root)) {
        if (root.dataset.sbMotionInitial === 'reveal') revealOnNextPaint(root);
        else setRootState(root, 'static');
        return;
      }
      setRootState(root, 'pending');
      const observer = root.matches(PROGRESS_ROOT_SELECTOR) ? ensureProgressObserver() : ensureObserver();
      observer.observe(root);
    });
  };

  const resetScope = (scope) => {
    collectRoots(scope).forEach(clearRootState);
    initScope(scope);
  };

  const revealEverything = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => setRootState(root, 'static'));
  };

  const initialize = () => initScope(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event && event.target instanceof Element) initScope(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof Element) collectRoots(event.target).forEach(clearRootState);
  });

  document.addEventListener('sb:motion:init', (event) => {
    if (event && event.target instanceof Element) initScope(event.target);
  });

  document.addEventListener('sb:motion:reset', (event) => {
    if (event && event.target instanceof Element) resetScope(event.target);
  });

  reducedMotionQuery.addEventListener?.('change', (event) => {
    if (event.matches) revealEverything();
  });

  let resizeFrame = 0;
  window.addEventListener('resize', () => {
    window.cancelAnimationFrame(resizeFrame);
    resizeFrame = window.requestAnimationFrame(() => {
      initScope(document);
    });
  }, { passive: true });

  window.SBSectionMotion = Object.freeze({
    initScope,
    resetScope,
  });
})();
